import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { $declinedAuthStore } from '@lib/store';
import { AlertTypeEnum, type Recipient } from '@shared/api/rest/alerts';
import { AddRecipientModal } from './addRecipientModal';
import { Button, ColorsEnum, Icon, Modal, Spinner, Text } from '@shared/widgets';
import CrossIcon from '@assets/icons/cross.svg';

interface Props {
  show: boolean;
  accountId: number;
  onClose: () => void;
  recipients?: Recipient[];
  subId?: string;
}

export const EditRecipientsModal = (props: Props) => {
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const declinedAuthStore = useUnit($declinedAuthStore.store);
  const recipients = declinedAuthStore.recipient;

  useEffect(() => {
    $declinedAuthStore.events.get(props.recipients!);
  }, [props.recipients, props.show]);

  useEffect(() => {
    return () => {
      $declinedAuthStore.events.clear();
    };
  }, []);

  const save = async () => {
    if (!props.accountId) return;
    try {
      setIsLoading(true);
      if (props.subId) {
        const request = { subscriptionId: props.subId, request: { recipients, override: true } };
        await $declinedAuthStore.events.updateDeclinedAuth({ request });
      } else {
        const request = { recipients, accountId: props.accountId };
        await $declinedAuthStore.events.addDeclinedAuth({ request });
      }
    } finally {
      setIsLoading(false);
      props.onClose();
    }
  };

  return (
    <>
      <Modal isOpen={props.show} onClose={props.onClose} title='Declined Authorisations alert'>
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className='mb-5'>
              <Text className='mb-3 ml-1' size='sm'>
                Recipients
              </Text>
              <div className='flex flex-col items-start -mb-2'>
                {recipients.map((r, i) => (
                  <div key={i} className='flex items-center p-2 pl-3 w-max-full mb-2 rounded-sm dark:bg-slate bg-white'>
                    <div className='shrink min-w-0 pr-4'>
                      <Text className='text-ellipsis' size='sm'>
                        {r.firstName}
                      </Text>
                      <Text className='text-ellipsis' size='xs' color={ColorsEnum.Gray}>
                        {r.email}
                      </Text>
                    </div>
                    <Button className='flex items-center justify-center w-4 h-4 !p-0' onClick={() => $declinedAuthStore.events.remove(r.email!)}>
                      <Icon icon={<CrossIcon />} color='red' />
                    </Button>
                  </div>
                ))}
              </div>
              <div className='mt-2'>
                <a onClick={() => setShowAddModal(true)} className='underline text-soft-teal text-sm leading-6 cursor-pointer'>
                  Add new recipient
                </a>
              </div>
            </div>
            <div className='mt-7'>
              <Button color='primary' fullWidth disabled={props.subId === undefined && recipients.length === 0} onClick={save}>
                Confirm
              </Button>
            </div>
          </>
        )}
      </Modal>
      <AddRecipientModal show={showAddModal} onClose={() => setShowAddModal(false)} alertType={AlertTypeEnum.declinedAuth} />
    </>
  );
};
